// pages/RequestQuotePage.tsx
import React, { useState, FormEvent, useRef } from 'react';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import HoneypotField from '../components/ui/HoneypotField';
import Section from '../components/layout/Section';
import { trackQuoteFormSubmission, trackBlockedSubmission } from '../utils/tracking';
import { checkSubmission, HONEYPOT_FIELD, SpamCheck } from '../utils/spamGate';
import { stateOptions } from '../data/constants';
import { featureFlags } from '../config/featureFlags';

const RequestQuotePage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const renderedAt = useRef<number>(Date.now());

  // Your actual Google Form URL for the estimate form
  const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/12k88kWf7LBjvFBDLZNNJ49YtAZuXMGNoXJed7LhHoJM/formResponse';

  // Field mappings from your Google Form HTML
  const FORM_FIELDS = {
    fullName: 'entry.57358285',
    email: 'entry.573633533',
    phone: 'entry.1443653866',
    address: 'entry.1319218342',
    city: 'entry.1568890873',
    state: 'entry.1190640500',
    zipCode: 'entry.2025364703',
    service: 'entry.1627881636',
    howDidYouHear: 'entry.298835975'
  };

  const serviceNeededOptions = [
    { value: "New Roof", label: "New Roof Installation" },
    { value: "Roof Repair", label: "Roof Repair (Leaks, Damage, etc.)" },
    { value: "Roof Inspection", label: "Roof Inspection" },
    { value: "Storm Damage Assessment", label: "Storm Damage Assessment" },
    { value: "Other", label: "Other (Please describe below)" },
  ];

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(event.currentTarget);

    const spam: SpamCheck = featureFlags.spamGateEnabled
      ? checkSubmission({
          honeypot: formData.get(HONEYPOT_FIELD) as string,
          name: formData.get('fullName') as string,
          city: formData.get('city') as string,
          state: formData.get('state') as string,
          elapsedMs: Date.now() - renderedAt.current,
        })
      : { verdict: 'clean', reasons: [] };

    // Honeypot hit: nothing goes to Google, nothing is reported as a conversion.
    if (spam.verdict === 'blocked') {
      trackBlockedSubmission('quote', spam);
      setIsSubmitted(true);
      formRef.current?.reset();
      setTimeout(() => setIsSubmitted(false), 5000);
      setIsSubmitting(false);
      return;
    }

    // Create FormData for Google Forms
    const googleFormData = new FormData();
    googleFormData.append(FORM_FIELDS.fullName, formData.get('fullName') as string);
    googleFormData.append(FORM_FIELDS.email, formData.get('email') as string);
    googleFormData.append(FORM_FIELDS.phone, formData.get('phone') as string);
    googleFormData.append(FORM_FIELDS.address, formData.get('address') as string);
    googleFormData.append(FORM_FIELDS.city, formData.get('city') as string);
    googleFormData.append(FORM_FIELDS.state, formData.get('state') as string);
    googleFormData.append(FORM_FIELDS.zipCode, formData.get('zipCode') as string);
    googleFormData.append(FORM_FIELDS.service, formData.get('servicesNeeded') as string);
    googleFormData.append(FORM_FIELDS.howDidYouHear, formData.get('howDidYouHear') as string);

    try {
      // Submit to Google Forms (using no-cors mode)
      await fetch(GOOGLE_FORM_URL, {
        method: 'POST',
        body: googleFormData,
        mode: 'no-cors' // This bypasses CORS but you won't get a response
      });

      // Track conversion in GA4. Suppressed for flagged submissions so junk
      // never becomes Smart Bidding training data.
      trackQuoteFormSubmission(formData.get('servicesNeeded') as string, spam);

      // Since we can't get a response due to CORS, assume success after request
      setIsSubmitted(true);

      // Reset form using ref
      if (formRef.current) {
        formRef.current.reset();
      }

      setTimeout(() => setIsSubmitted(false), 7000);

    } catch (error) {
      // Even if there's a network error, the form likely submitted successfully
      setIsSubmitted(true);

      // Reset form using ref
      if (formRef.current) {
        formRef.current.reset();
      }

      setTimeout(() => setIsSubmitted(false), 7000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      <Section title="Request Your Free Roofing Estimate" bg="bg-slate-50">
        <p className="text-center text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
          Ready to start your roofing project in Avon Park, FL? Provide us with a few details below, and one of our experienced specialists will contact you to discuss your needs and schedule a free, comprehensive estimate.
        </p>
        <div className="max-w-2xl mx-auto bg-white p-8 md:p-12 rounded-xl shadow-2xl">
          {isSubmitted && (
            <div className="mb-6 p-4 bg-green-100 text-green-700 border border-green-300 rounded-md">
              Thank you for your quote request! We will contact you within 24 business hours.
            </div>
          )}
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
            <HoneypotField />
            <div className="grid md:grid-cols-2 gap-6">
              <FormField label="Full Name" name="fullName" required placeholder="John M. Doe" />
              <FormField label="Email Address" name="email" type="email" required placeholder="your.email@example.com" />
            </div>
            <FormField label="Phone Number" name="phone" type="tel" required placeholder="(863) 555-0123" />
            <FormField label="Street Address" name="address" required placeholder="123 Main St" />
            <div className="grid md:grid-cols-3 gap-6">
              <FormField label="City" name="city" required placeholder="Avon Park" />
              <FormField label="State" name="state" as="select" options={stateOptions} required placeholder="-- Select State --" />
              <FormField label="Zip Code" name="zipCode" required placeholder="33825" />
            </div>
            <FormField label="Service(s) Needed" name="servicesNeeded" as="select" options={serviceNeededOptions} required placeholder="-- Select Service --" />
            <FormField label="How Did You Hear About Us?" name="howDidYouHear" placeholder="E.g., Friend, Google, Saw our truck" />
            <div>
              <Button
                type="submit"
                variant="primary"
                className="w-full text-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
              </Button>
            </div>
          </form>
          <div className="mt-10 pt-6 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-slate-700 mb-3">What to Expect Next:</h3>
            <ol className="list-decimal list-inside text-gray-600 space-y-1">
              <li>We'll contact you within <strong>24 business hours</strong> to discuss your project.</li>
              <li>We may schedule a convenient time for an on-site inspection if necessary.</li>
              <li>You'll receive a detailed, transparent quote for the proposed work.</li>
            </ol>
            <p className="mt-4 text-gray-600"><strong>We look forward to serving you!</strong></p>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default RequestQuotePage;
